// 「기간 예상 기공료」 서버 호출·문구·검증 (2026-10-07)
// 계약: docs/계약_기간예상명세서_웹_2026-10-07.md (서버 functions/lib/expected-statement.js · callable getExpectedStatement)
// 기획: docs/기획서_치과_기간예상명세서_2026-10-07.md §3-3 · 법무: docs/법무검토_치과_기간예상명세서_2026-10-07.md §3 (☆ 확정 문안)
// - 치과가 [보기]를 눌러야 1회 호출(자동 호출 금지). Firestore 쓰기 0.
// - 응답의 disclaimer·patientName 대체 문구는 서버 값 그대로 표시(웹 사전 없음). 기공소 [품목 보기](서버 호출 없음)만 아래 상수 사본을 쓴다.
import { getFunctions, httpsCallable } from 'firebase/functions';

const REGION = 'asia-northeast3';

/** 기간 상한(계약 §0): 포함 일수 ≤ 93 · 종료일 ≤ 오늘 · 시작일 ≥ 오늘−365일 */
export const EXPSTMT_MAX_DAYS = 93;
export const EXPSTMT_MAX_LOOKBACK_DAYS = 365;
/** 응답 행 상한(계약 §1 truncated) — 기공소 [품목 보기]도 같은 상한을 적용해 화면 규칙을 맞춘다 */
export const EXPSTMT_MAX_ROWS = 500;

// ☆ 서버 상수 사본 — functions/lib/expected-statement.js:41·:43 과 글자 동일해야 한다(기공소 [품목 보기]는 서버를 부르지 않아 여기 값을 쓴다).
//   치과 화면은 서버 응답값을 우선 사용하고 이 사본은 폴백.
export const EXPSTMT_DISCLAIMER = '기공소가 등록한 단가표와 이 기간 의뢰서·수집 작업으로 자동 계산한 참고용 예상치입니다. 견적서·청구서가 아니며, 실제 금액은 기공소가 발행한 거래명세서 기준입니다. 리메이크·단가 미등록 품목은 금액에 포함되지 않습니다. 내부 참고용이며 외부 공개·광고에 사용하지 마세요.';
export const EXPSTMT_UNATTRIBUTED_LABEL = '자동 수집 건(거래처 확인 전)';

// ☆ 화면 고정 문구 (법무 §3 확정)
/** C-1 카드 캡션 */
export const EXPSTMT_CARD_CAPTION = '기간과 기공소를 고르면, 그 기간 의뢰서와 기공소 프로그램이 수집한 작업을 기공소가 등록한 우리 치과 단가표로 기공소 검토 없이 자동 계산해 명세서 모양으로 보여줍니다. 작성일 기준입니다. 우리 치과 환자가 아닌 이름이 보이면 기공소에 알려 주세요.';
/** C-2 모달·인쇄 상단 안내 첫 문장(굵게) — 둘째 문장부터는 서버 disclaimer(C-3) */
export const EXPSTMT_NOTICE_HEAD = '예상 기공료 — 거래명세서가 아닙니다.';
/** C-4 */
export const EXPSTMT_SHEET_TITLE = '예 상 기 공 료 (참고용)';
export const EXPSTMT_BADGE = '예상 — 명세서 아님';
export const EXPSTMT_NUMBER_LABEL = '예상 (명세서 아님)';
/** C-5 끈 기공소 (10-07 /lab-cost 문구와 통일) */
export const EXPSTMT_NOT_SHARED = '이 기공소는 예상치를 표시하지 않습니다';
/** 결정 7 "안내만" — 단가표 없음 */
export const EXPSTMT_NO_PRICE = '이 기공소가 우리 치과 단가표를 아직 등록하지 않아 예상 금액을 계산할 수 없습니다';
/** 계약 §3 그 외(internal) */
export const EXPSTMT_INTERNAL_ERROR = '예상 명세서를 만들지 못했습니다. 잠시 후 다시 시도해 주세요';
export const EXPSTMT_NOT_CONNECTED = '연결된 기공소가 아닙니다';
export const EXPSTMT_RATE_LIMIT = '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요';

/** 'YYYY-MM-DD' 를 로컬 자정 Date 로 (UTC 해석으로 하루 밀리는 것 방지) */
export const dateFromYmd = (ymd) => {
    const m = String(ymd || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isFinite(d.getTime()) ? d : null;
};
export const toYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** 포함 일수 (from·to 둘 다 포함) */
export const inclusiveDays = (from, to) => {
    const a = dateFromYmd(from), b = dateFromYmd(to);
    if (!a || !b) return 0;
    return Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
};

/**
 * 서버와 같은 규칙으로 선검증 (계약 §0 — 불필요한 호출을 줄인다). 통과하면 null, 아니면 화면 문구(서버 message 와 같은 뜻).
 */
export function validateExpectedRange(from, to, labId, now = new Date()) {
    if (!labId) return '기공소를 선택해 주세요.';
    const a = dateFromYmd(from), b = dateFromYmd(to);
    if (!a || !b) return '시작일과 종료일을 입력해 주세요.';
    if (a.getTime() > b.getTime()) return '시작일이 종료일보다 늦을 수 없습니다.';
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (b.getTime() > today.getTime()) return '종료일은 오늘까지만 선택할 수 있습니다.';
    const lookback = new Date(today.getTime() - EXPSTMT_MAX_LOOKBACK_DAYS * 86400000);
    if (a.getTime() < lookback.getTime()) return `시작일은 오늘부터 ${EXPSTMT_MAX_LOOKBACK_DAYS}일 이내여야 합니다.`;
    if (inclusiveDays(from, to) > EXPSTMT_MAX_DAYS) return `기간은 최대 ${EXPSTMT_MAX_DAYS}일까지 볼 수 있습니다. 기간을 줄여 주세요.`;
    return null;
}

/** callable 호출 — 응답 data 그대로 반환(계약 §1·§2). 오류는 그대로 throw(호출처가 describeExpectedStatementError 로 문구화) */
export async function fetchExpectedStatement({ from, to, labId }) {
    const fn = httpsCallable(getFunctions(undefined, REGION), 'getExpectedStatement');
    const res = await fn({ from, to, labId });
    return res?.data || null;
}

/**
 * CF 오류 → { kind, text } (계약 §3). 원본은 여기서 console.error (조용한 실패 금지).
 *  kind: 'feature-off' | 'not-connected' | 'invalid' | 'permission' | 'rate' | 'unauthenticated' | 'not-deployed' | 'internal'
 */
export function describeExpectedStatementError(e) {
    console.error('[expectedStatementApi]', e?.code || '', e?.message || e, e?.details || '');
    const code = String(e?.code || '').replace(/^functions\//, '');
    if (code === 'failed-precondition') {
        if (e?.details?.code === 'feature-off') return { kind: 'feature-off', text: '' };
        return { kind: 'not-connected', text: EXPSTMT_NOT_CONNECTED };
    }
    if (code === 'invalid-argument') return { kind: 'invalid', text: e?.message || '입력값을 확인해 주세요.' };
    if (code === 'permission-denied') return { kind: 'permission', text: e?.message || '권한이 없습니다.' };
    if (code === 'resource-exhausted') return { kind: 'rate', text: EXPSTMT_RATE_LIMIT };
    if (code === 'unauthenticated') return { kind: 'unauthenticated', text: '로그인이 필요합니다.' };
    // 구 서버(함수 미배포)는 not-found 로 떨어진다 — "만들지 못했습니다"와 구분해 안내
    if (code === 'not-found') return { kind: 'not-deployed', text: '예상 기공료 기능이 아직 서버에 준비되지 않았습니다. 잠시 후 다시 시도해 주세요.' };
    return { kind: 'internal', text: EXPSTMT_INTERNAL_ERROR };
}

/** 인쇄 제안 파일명 — "예상기공료_{기공소}_{from}~{to}". 기공소 상호에 "명세서"가 들어가도 파일명에 남지 않게 치환(법무 §3 파일명 방어). 환자명 0 */
export function expectedPrintTitle(labName, from, to) {
    const safeLab = String(labName || '기공소').replace(/거래명세서|명세서/g, '').replace(/\s+/g, ' ').trim() || '기공소';
    return `예상기공료_${safeLab}_${from}~${to}`.replace(/[\\/:*?"<>|]/g, '_');
}
